/**
 * Base debug page class that provides common functionality for debug pages.
 * Users can extend this class to create custom debug pages.
 */

import type { IDebugPage, IDebugPageConfig } from '@gotti/core';
import type { ClientDebugSystem } from './ClientDebugSystem.js';

/**
 * Base class for debug pages.
 * Provides common utilities and structure for creating debug UI pages.
 */
export abstract class BaseDebugPage implements IDebugPage {
  protected element?: HTMLElement;
  
  constructor(
    public readonly config: IDebugPageConfig,
    protected readonly debugSystem: ClientDebugSystem
  ) {}

  /**
   * Create the page element. Must be implemented by subclasses.
   */
  abstract createElement(): HTMLElement;

  /**
   * Called when the page becomes active/visible.
   */
  onActivate?(): void;

  /**
   * Called when the page becomes inactive/hidden.
   */
  onDeactivate?(): void;

  /**
   * Called on each update tick.
   */
  onUpdate?(delta: number): void;

  /**
   * Cleanup when page is destroyed.
   */
  destroy?(): void;

  // ========================================
  // Utility Methods for Building UI
  // ========================================

  /**
   * Create a styled container div.
   */
  protected createContainer(styles: Partial<CSSStyleDeclaration> = {}): HTMLDivElement {
    const div = document.createElement('div');
    Object.assign(div.style, {
      padding: '10px',
      ...styles,
    });
    return div;
  }

  /**
   * Create a section with a header.
   */
  protected createSection(title: string, styles: Partial<CSSStyleDeclaration> = {}): HTMLDivElement {
    const section = document.createElement('div');
    Object.assign(section.style, {
      marginBottom: '15px',
      ...styles,
    });

    const header = document.createElement('h3');
    header.textContent = title;
    Object.assign(header.style, {
      margin: '0 0 10px 0',
      fontSize: '14px',
      color: '#aaa',
      borderBottom: '1px solid #444',
      paddingBottom: '5px',
    });
    section.appendChild(header);

    return section;
  }

  /**
   * Create a key-value display row.
   */
  protected createKeyValue(key: string, value: string, container: HTMLElement): HTMLDivElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '3px 0',
      borderBottom: '1px solid #333',
    });

    const keySpan = document.createElement('span');
    keySpan.textContent = key;
    keySpan.style.color = '#888';

    const valueSpan = document.createElement('span');
    valueSpan.textContent = value;
    valueSpan.style.color = '#fff';

    row.appendChild(keySpan);
    row.appendChild(valueSpan);
    container.appendChild(row);

    return row;
  }

  /**
   * Create a scrollable list container.
   */
  protected createScrollableList(maxHeight: string = '200px'): HTMLDivElement {
    const list = document.createElement('div');
    Object.assign(list.style, {
      maxHeight,
      overflowY: 'auto',
      backgroundColor: '#1a1a1a',
      borderRadius: '4px',
      padding: '5px',
    });
    return list;
  }

  /**
   * Create a button.
   */
  protected createButton(
    label: string,
    onClick: () => void,
    styles: Partial<CSSStyleDeclaration> = {}
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = label;
    Object.assign(button.style, {
      padding: '5px 10px',
      backgroundColor: '#444',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginRight: '5px',
      ...styles,
    });
    button.onclick = onClick;
    return button;
  }

  /**
   * Format a log entry for display.
   */
  protected formatLogEntry(log: { timestamp: number; level: string; args: any[] }): string {
    const time = new Date(log.timestamp).toLocaleTimeString();
    const level = log.level.toUpperCase().padEnd(5);
    const message = log.args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    return `[${time}] ${level} ${message}`;
  }

  /**
   * Get log level color.
   */
  protected getLogLevelColor(level: string): string {
    switch (level) {
      case 'error': return '#f55';
      case 'warn': return '#fa0';
      case 'info': return '#5af';
      default: return '#aaa';
    }
  }
}
